import React, { useEffect, useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  setDoc,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { firestore } from '../../database/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEye,
  HiTrash,
  HiXMark,
  HiUser,
  HiEnvelope,
  HiPhone,
  HiCheckCircle,
  HiExclamationTriangle,
  HiInformationCircle,
  HiArrowPath,
  HiBuildingOffice2,
  HiMagnifyingGlass,
  HiShieldCheck,
  HiAcademicCap,
  HiUserGroup,
  HiLockClosed,
  HiKey,
  HiClock,
} from 'react-icons/hi2';
import { useUserRole } from '../../context/UserRoleContext';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -50, scale: 0.95 }}
    className={`fixed right-4 top-4 z-50 flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg ${
      type === 'success'
        ? 'border border-green-200 bg-gray-50'
        : type === 'error'
          ? 'border border-red-200 bg-red-50'
          : 'border border-gray-200 bg-gray-50'
    }`}
  >
    {type === 'success' && <HiCheckCircle className="h-5 w-5 text-green-600" />}
    {type === 'error' && <HiExclamationTriangle className="h-5 w-5 text-red-600" />}
    {type === 'info' && <HiInformationCircle className="h-5 w-5 text-blue-600" />}
    <span
      className={`text-sm font-medium ${type === 'success' ? 'text-green-800' : type === 'error' ? 'text-red-800' : 'text-black'}`}
    >
      {message}
    </span>
    <button
      onClick={onClose}
      className={`ml-2 hover:opacity-70 ${type === 'success' ? 'text-green-600' : type === 'error' ? 'text-red-600' : 'text-gray-600'}`}
    >
      <HiXMark className="h-4 w-4" />
    </button>
  </motion.div>
);

const DeletedUsersTable = () => {
  const { userRole } = useUserRole();
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [restoring, setRestoring] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [userToRestore, setUserToRestore] = useState(null);
  const [restorePassword, setRestorePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    user: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const canManageUsers = ['admin', 'superAdmin', 'moderator', 'coach', 'businessadvisor'].includes(
    userRole
  );

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getUserRoleLabel = (role, originalCollection) => {
    if (role) return role;
    if (originalCollection === 'clients') return 'Client';
    if (originalCollection === 'moderator') return 'Business Advisor';
    if (originalCollection === 'coaches') return 'Coach/Mentor';
    if (originalCollection === 'admin') return 'Admin';
    if (originalCollection === 'alumni') return 'Alumni';
    return 'User';
  };

  const getUserRoleColor = (role, originalCollection) => {
    const userRole = role || originalCollection;
    if (userRole === 'admin' || originalCollection === 'admin') {
      return 'bg-red-100 text-red-700 border-red-200';
    }
    if (userRole === 'moderator' || originalCollection === 'moderator') {
      return 'bg-purple-100 text-purple-700 border-purple-200';
    }
    if (userRole === 'coach' || originalCollection === 'coaches') {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    }
    if (userRole === 'alumni' || originalCollection === 'alumni') {
      return 'bg-gray-100 text-gray-700 border-gray-200';
    }
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const fetchDeletedUsers = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(firestore, 'deleted_users'));
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setDeletedUsers(data);
      if (data.length === 0) {
        showToast('No deleted users found', 'info');
      }
    } catch (err) {
      setError('Failed to fetch deleted users');
      showToast('Failed to load deleted users', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Real-time listener
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(firestore, 'deleted_users'),
      snapshot => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setDeletedUsers(data);
      },
      err => {
        showToast('Error listening to deleted users', 'error');
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchDeletedUsers();
  }, [fetchDeletedUsers]);

  const handleView = user => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleDeleteClick = user => {
    setDeleteConfirm({
      isOpen: true,
      user,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.user) return;

    setIsDeleting(true);
    try {
      await deleteDoc(doc(firestore, 'deleted_users', deleteConfirm.user.id));
      setDeletedUsers(prev => prev.filter(u => u.id !== deleteConfirm.user.id));
      showToast('User permanently deleted', 'success');
      setDeleteConfirm({ isOpen: false, user: null });
      fetchDeletedUsers();
    } catch (err) {
      showToast(`Failed to delete user: ${err.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, user: null });
  };

  const handleRestore = async user => {
    setUserToRestore(user);
    setRestorePassword('');
    setShowRestoreModal(true);
  };

  const handleRestoreSubmit = async () => {
    if (!userToRestore) return;

    // If user needs a password (for clients, moderators, coaches), require it
    const needsPassword =
      userToRestore.originalCollection === 'clients' ||
      userToRestore.originalCollection === 'moderator' ||
      userToRestore.originalCollection === 'coaches';

    if (needsPassword && (!restorePassword || restorePassword.length < 6)) {
      showToast('Password must be at least 6 characters long', 'error');
      return;
    }

    setRestoring(true);
    try {
      const userData = { ...userToRestore };
      delete userData.id;
      delete userData.originalCollection;
      delete userData.deletedAt;
      delete userData.deletedBy;
      delete userData.restored;

      // Determine the target collection
      let targetCollection = userToRestore.originalCollection;
      if (targetCollection === 'moderator') {
        targetCollection = 'moderator';
      } else if (targetCollection === 'coaches') {
        targetCollection = 'coaches';
      } else if (targetCollection === 'clients') {
        targetCollection = 'clients';
      } else if (targetCollection === 'admin') {
        targetCollection = 'admin';
      } else if (targetCollection === 'alumni') {
        targetCollection = 'alumni';
      }

      // If user has a UID and needs password, recreate auth account
      if (userToRestore.uid && needsPassword) {
        try {
          const tempApp = initializeApp(
            {
              apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
              authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
              projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
              storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
              messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
              appId: process.env.REACT_APP_FIREBASE_APP_ID,
            },
            'temp-app-restore-' + Date.now()
          );

          const tempAuth = getAuth(tempApp);

          // Try to create user with email and password
          try {
            await createUserWithEmailAndPassword(
              tempAuth,
              userToRestore.email || userToRestore.userEmail,
              restorePassword
            );
          } catch (authError) {
            if (authError.code === 'auth/email-already-in-use') {
            } else {
              throw authError;
            }
          }

          await tempAuth.signOut();
        } catch (authError) {
          // If auth creation fails but email already exists, continue with Firestore restoration
          if (authError.code !== 'auth/email-already-in-use') {
            throw authError;
          }
        }
      }

      // Restore user data to original collection
      userData.restoredAt = new Date().toISOString();
      userData.restoredBy = userRole || 'admin';
      userData.status = userData.status || 'active';

      await setDoc(
        doc(firestore, targetCollection, userToRestore.uid || userToRestore.id),
        userData
      );

      // Remove from deleted_users
      await deleteDoc(doc(firestore, 'deleted_users', userToRestore.id));

      setDeletedUsers(prev => prev.filter(u => u.id !== userToRestore.id));
      setShowRestoreModal(false);
      setUserToRestore(null);
      setRestorePassword('');
      showToast(
        `${getUserRoleLabel(userToRestore.role, userToRestore.originalCollection)} restored successfully!`,
        'success'
      );
      fetchDeletedUsers();
    } catch (error) {
      let errorMessage = 'Failed to restore user';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'User already exists. Please check the system.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      showToast(errorMessage, 'error');
    } finally {
      setRestoring(false);
    }
  };

  const filteredUsers = deletedUsers.filter(user => {
    const matchesSearch =
      searchTerm === '' ||
      `${user.firstname || user.firstName || ''} ${user.lastname || user.lastName || ''}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.organization?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole =
      filterRole === 'all' || user.originalCollection === filterRole || user.role === filterRole;

    return matchesSearch && matchesRole;
  });

  if (!canManageUsers) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <HiExclamationTriangle className="mx-auto mb-4 h-16 w-16 text-red-500" />
          <h2 className="mb-2 text-2xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">
            You don't have permission to access the deleted users management system.
          </p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      label: 'Total Deleted',
      value: deletedUsers.length,
      icon: <HiUser className="h-6 w-6 text-red-600" />,
      bg: 'bg-red-100',
    },
    {
      label: 'Clients',
      value: deletedUsers.filter(u => u.originalCollection === 'clients').length,
      icon: <HiUser className="h-6 w-6 text-blue-600" />,
      bg: 'bg-blue-100',
    },
    {
      label: 'Business Advisors',
      value: deletedUsers.filter(u => u.originalCollection === 'moderator').length,
      icon: <HiUserGroup className="h-6 w-6 text-purple-600" />,
      bg: 'bg-purple-100',
    },
    {
      label: 'Coaches',
      value: deletedUsers.filter(u => u.originalCollection === 'coaches').length,
      icon: <HiAcademicCap className="h-6 w-6 text-green-600" />,
      bg: 'bg-green-100',
    },
    {
      label: 'Alumni',
      value: deletedUsers.filter(u => u.originalCollection === 'alumni').length,
      icon: <HiUser className="h-6 w-6 text-gray-600" />,
      bg: 'bg-gray-100',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Deleted Users</h1>
              <p className="mt-2 text-sm text-gray-600 md:text-base">
                View and restore deleted users. Users can be restored back to their original
                collections.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <HiMagnifyingGlass className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <select
                value={filterRole}
                onChange={e => setFilterRole(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="all">All Roles</option>
                <option value="clients">Clients</option>
                <option value="moderator">Business Advisors</option>
                <option value="coaches">Coaches</option>
                <option value="alumni">Alumni</option>
                <option value="admin">Admins</option>
              </select>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="flex items-center gap-2">
                <HiExclamationTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading deleted users...</p>
            </div>
          ) : deletedUsers.length === 0 ? (
            <div className="p-8 text-center">
              <HiUser className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-black">No deleted users found</h3>
              <p className="mt-1 text-gray-600">
                Deleted users will appear here once they are deleted from the system.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4" /> Name
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiEnvelope className="h-4 w-4" /> Email
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiBuildingOffice2 className="h-4 w-4" /> Role
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiPhone className="h-4 w-4" /> Phone
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiClock className="h-4 w-4" /> Deleted At
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredUsers.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-gray-500 to-gray-600 text-white">
                            {(user.firstname ||
                              user.firstName ||
                              user.email ||
                              user.userEmail ||
                              user.id)?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-black">
                              {user.firstname || user.firstName || ''}{' '}
                              {user.lastname || user.lastName || ''}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {user.uid || user.id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{user.email || user.userEmail}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getUserRoleColor(user.role, user.originalCollection)}`}
                        >
                          {getUserRoleLabel(user.role, user.originalCollection)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{user.phone || 'N/A'}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {user.deletedAt
                          ? new Date(user.deletedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                        <div className="flex items-center justify-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleView(user)}
                            className="rounded-lg bg-gray-100 p-2 text-gray-700 transition-colors hover:bg-gray-200"
                            title="View Details"
                          >
                            <HiEye className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleRestore(user)}
                            className="rounded-lg bg-green-100 p-2 text-green-700 transition-colors hover:bg-green-200"
                            title="Restore User"
                          >
                            <HiArrowPath className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDeleteClick(user)}
                            className="rounded-lg bg-red-100 p-2 text-red-700 transition-colors hover:bg-red-200"
                            title="Permanently Delete"
                          >
                            <HiTrash className="h-4 w-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {stats.map((stat, idx) => (
            <div
              key={stat.label}
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-black">{stat.value}</p>
                </div>
                <div className={`rounded-full p-3 ${stat.bg}`}>{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>

        <AnimatePresence>
          {showModal && selectedUser && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 shadow-2xl md:max-w-2xl"
              >
                <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-gray-500 to-gray-600 text-lg font-bold text-white shadow-sm">
                      {(selectedUser.firstname ||
                        selectedUser.firstName ||
                        selectedUser.email ||
                        selectedUser.id)?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-base font-semibold text-black">
                          {selectedUser.firstname || selectedUser.firstName || ''}{' '}
                          {selectedUser.lastname || selectedUser.lastName || ''}
                        </span>
                        <span className="text-xs text-gray-500">
                          {selectedUser.email || selectedUser.userEmail}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                    onClick={() => setShowModal(false)}
                    aria-label="Close"
                  >
                    <HiXMark className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-8 p-6">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiEnvelope className="h-4 w-4 text-blue-600" /> Email
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedUser.email || selectedUser.userEmail}
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiPhone className="h-4 w-4 text-blue-600" /> Phone
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedUser.phone || 'N/A'}
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiShieldCheck className="h-4 w-4 text-blue-600" /> Role
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium ${getUserRoleColor(selectedUser.role, selectedUser.originalCollection)}`}
                        >
                          {getUserRoleLabel(selectedUser.role, selectedUser.originalCollection)}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-blue-900/80">
                        <HiBuildingOffice2 className="h-4 w-4 text-blue-600" /> Original Collection
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        {selectedUser.originalCollection || 'N/A'}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-red-900">
                      <HiExclamationTriangle className="h-4 w-4" />
                      Deletion Information
                    </h4>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-medium text-gray-600">Deleted At</label>
                        <p className="text-sm font-semibold text-black">
                          {selectedUser.deletedAt
                            ? new Date(selectedUser.deletedAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600">Deleted By</label>
                        <p className="text-sm font-semibold text-black">
                          {selectedUser.deletedBy || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {selectedUser.organization && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <HiBuildingOffice2 className="h-4 w-4" />
                        Organization
                      </h4>
                      <p className="text-sm text-gray-700">{selectedUser.organization}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showRestoreModal && userToRestore && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
              >
                <div className="mb-6">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white">
                      <HiArrowPath className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Restore User</h3>
                      <p className="text-sm text-gray-600">
                        Restore {userToRestore.firstname || userToRestore.firstName || ''}{' '}
                        {userToRestore.lastname || userToRestore.lastName || ''} to{' '}
                        {getUserRoleLabel(userToRestore.role, userToRestore.originalCollection)}
                      </p>
                    </div>
                  </div>
                </div>

                {(userToRestore.originalCollection === 'clients' ||
                  userToRestore.originalCollection === 'moderator' ||
                  userToRestore.originalCollection === 'coaches') && (
                  <div className="space-y-6">
                    <div className="rounded-lg bg-blue-50 p-4">
                      <div className="mb-2 flex items-center gap-2">
                        <HiLockClosed className="h-5 w-5 text-blue-600" />
                        <span className="font-medium text-blue-900">Set Account Password</span>
                      </div>
                      <p className="mb-4 text-sm text-blue-700">
                        This password will be used by the user to login to their restored account.
                      </p>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <HiKey className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="password"
                          value={restorePassword}
                          onChange={e => setRestorePassword(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          placeholder="Enter secure password (min 6 characters)"
                          minLength={6}
                        />
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Password requirements: Minimum 6 characters
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowRestoreModal(false);
                      setUserToRestore(null);
                      setRestorePassword('');
                    }}
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-700 transition-colors hover:bg-gray-50"
                    disabled={restoring}
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleRestoreSubmit}
                    disabled={
                      restoring ||
                      ((userToRestore.originalCollection === 'clients' ||
                        userToRestore.originalCollection === 'moderator' ||
                        userToRestore.originalCollection === 'coaches') &&
                        (!restorePassword || restorePassword.length < 6))
                    }
                    className="flex-1 rounded-lg bg-gradient-to-r from-green-600 to-green-700 px-4 py-3 text-white shadow-lg transition-all hover:shadow-xl disabled:opacity-50 disabled:hover:scale-100"
                  >
                    <div className="flex items-center justify-center gap-2">
                      {restoring ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          <span>Restoring...</span>
                        </>
                      ) : (
                        <>
                          <HiArrowPath className="h-5 w-5" />
                          <span>Restore User</span>
                        </>
                      )}
                    </div>
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Permanently Delete User"
          message={`Are you sure you want to permanently delete ${deleteConfirm.user?.firstname || deleteConfirm.user?.firstName || ''} ${deleteConfirm.user?.lastname || deleteConfirm.user?.lastName || ''}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default DeletedUsersTable;
